import { ReactNode } from 'react';
import { useTheme } from 'styled-components';
import {
    Badge,
    Body,
    ExpandableContent,
    getSpacingToken,
    IconButton,
    IconName,
    Stack,
    Typography,
} from '@optimus-web/core';
import { HighlightIcon } from '../HighlightIcon';
import { Banner, Label } from './style';

interface SmartTipsBaseProps {
    label?: ReactNode;
    title?: ReactNode;
    disclaimer?: ReactNode;
    textLessButton: ReactNode;
    textMoreButton: ReactNode;
    badge?: string;
    lines?: number;
    details?: ReactNode;
    onClose?: (event: React.MouseEvent) => void;
    actions?: ReactNode;
    children: ReactNode;
    hideIcon?: boolean;
}
type SmartTipsProps = SmartTipsBaseProps &
    ({ variant?: 'notification'; size?: never } | { variant?: 'banner'; size?: 'medium' | 'large' });

export const SmartTip = ({
    size = 'medium',
    variant = 'banner',
    label,
    title,
    disclaimer,
    textMoreButton,
    textLessButton,
    badge,
    lines = 2,
    onClose,
    actions,
    details,
    children,
    hideIcon,
}: SmartTipsProps) => {
    const theme = useTheme();

    return (
        <Banner distribution="stretch" variant={variant} spacing="150">
            {!hideIcon && (
                <HighlightIcon
                    icon={IconName.BulbStars}
                    size={variant === 'notification' ? 'small' : 'medium'}
                    variant="info"
                />
            )}
            <Stack vertical itemAlignment="start" spacing="50">
                <Stack vertical spacing="25">
                    {variant === 'banner' ? (
                        <Stack spacing="50" distribution="space-between">
                            <Stack vertical spacing="25">
                                <Label
                                    textStyle={size === 'medium' ? 'bodyExtraSmallStrong' : 'bodySmallStrong'}
                                    as="div"
                                >
                                    {label}
                                </Label>
                                {title && (
                                    <Stack spacing="50" itemAlignment="center">
                                        <Typography
                                            textStyle={size === 'medium' ? 'bodyMediumStrong' : 'bodyLargeStrong'}
                                            as="div"
                                        >
                                            {title}
                                        </Typography>
                                        {badge && <Badge variant="subtle" content={badge} outline />}
                                    </Stack>
                                )}
                            </Stack>
                            {onClose && (
                                <IconButton
                                    variant="tertiary"
                                    size="small"
                                    icon={IconName.CrossClose}
                                    onClick={onClose}
                                    type="submit"
                                />
                            )}
                        </Stack>
                    ) : null}
                    <ExpandableContent
                        lines={lines}
                        expandedToggleText={textLessButton}
                        collapsedToggleText={textMoreButton}
                        lineHeight={size === 'large' ? getSpacingToken('300')({ theme }) : undefined}
                    >
                        <Stack vertical fill spacing="50">
                            <Body size={size === 'large' ? 'medium' : 'small'}>{children}</Body>
                            {details && (
                                <Typography textStyle="bodySmall" variant="tertiary" as="div">
                                    {details}
                                </Typography>
                            )}
                        </Stack>
                    </ExpandableContent>
                </Stack>
                {(variant === 'banner' && actions) || null}
                {variant === 'banner' && (
                    <Typography textStyle="bodySmall" variant="tertiary" as="div">
                        {disclaimer}
                    </Typography>
                )}
            </Stack>
        </Banner>
    );
};
